/**
 * Created by Alex Poh. on 20/04/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { ISAChart, ISAChartSubject} from '../school-admin-dashboard.interface';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { SchoolAdminDashboardService } from '../school-admin-dashboard.service';

@Component({
  selector: 'curr-school-statistic-super-admin',
  templateUrl: './school-statistic-super-admin.component.html',
  styleUrls: ['./school-statistic-super-admin.component.scss']
})
export class SchoolStatisticSuperAdminComponent implements OnInit {

  @Output() studentsTableFiltersChange = new EventEmitter<{ subject: string; studyYearId: number; courseId: number, schoolYearSettingsId?: number }>();
  @Output() assessmentResultsMarkChange = new EventEmitter<string>();

  chartAverageBenchmark: string;

  teachersDropdownDisabled = true;
  subjectsDropdownDisabled = true;

  assessmentSubjects = [];
  assessmentChartSchools = [];
  assessmentChartTeachers = [];
  assessmentChartSubjects = [];
  assessmentResultsMark = '';
  assessmentResultDate = '';
  assessmentResultsChart = {
    grades: [],
    averageBenchmark: [],
  };
  assessmentResultsChartMonth = [];
  assessmentChartLoading = true;
  assessmentChartNoData = false;
  assessmentChartSelectedSubject: ISAChartSubject;

  schoolSelectCount = 0;
  teacherSelectCount = 0;

  studentsTableFilters: { subject: string; studyYearId: number; courseId: number, schoolYearSettingsId?: number };

  unsubscribe: Subject<void> = new Subject<void>();

  constructor(private router: Router, private schoolAdminDashboardService: SchoolAdminDashboardService) {}

  ngOnInit(): void {
    this.getAssessmentChartData();
  }

  assessmentChartSchoolChange(value: ISAChartSubject) {
    this.assessmentChartTeachers = [];

    const uniqueTeachersBySchool = this.assessmentSubjects.filter(
      (item, index, self) =>
        item.schoolYearSettingsId === value.schoolYearSettingsId &&
        index === self.findIndex(
          (t) =>
            t.teacherName === item.teacherName
        )
    );

    uniqueTeachersBySchool.forEach((item) => {
      this.assessmentChartTeachers.push({ value: item, label: item.teacherName });
    });

    if (this.schoolSelectCount > 0 && this.assessmentChartTeachers.length > 0) {
      this.assessmentChartTeacherChange(this.assessmentChartTeachers[0].value);
    }

    this.teachersDropdownDisabled = false;
    this.schoolSelectCount++;
  }

  assessmentChartTeacherChange(value: ISAChartSubject) {
    this.assessmentChartSubjects = [];
    const subjectsByTeacher = this.assessmentSubjects.filter((item) => item.teacherId === value.teacherId);

    subjectsByTeacher.forEach((item) => {
      this.assessmentChartSubjects.push({ value: item, label: item.label });
    });

    if (this.teacherSelectCount > 0 && this.assessmentChartSubjects.length > 0) {
      this.assessmentChartSubjectsChange(this.assessmentChartSubjects[0].value);
    }

    this.subjectsDropdownDisabled = false;
    this.teacherSelectCount++;
  }

  assessmentChartSubjectsChange(value: ISAChartSubject) {
    this.assessmentChartSelectedSubject = value;
    this.studentsTableFilters = { subject: value.subject, studyYearId: value.studyYearId, courseId: value.courseId, schoolYearSettingsId: value.schoolYearSettingsId };
    this.studentsTableFiltersChange.emit(this.studentsTableFilters);
    this.getAssessmentChartWithoutSubjects(value.subject, value.studyYearId, value.courseId, value.schoolYearSettingsId);
  }

  private getAssessmentChartData(): void {
    this.schoolAdminDashboardService
      .getAssessmentChartSubjects()
      .pipe(
        switchMap((subjects) => {
          if (subjects.length > 0) {
            this.assessmentSubjects = subjects;

            const uniqueBySchoolName = subjects.filter(
              (item, index, self) => index === self.findIndex((t) => t.schoolName === item.schoolName));

            uniqueBySchoolName.forEach((item) => {
              this.assessmentChartSchools.push({ value: item, label: item.schoolName });
            });

            this.assessmentChartLoading = false;
            return of();
          }
          this.assessmentChartNoData = true;
          return of();
        }),
        takeUntil(this.unsubscribe),
      )
      .subscribe(
        (chartData) => {
          if (chartData) {
            this.processAssessmentChart(chartData);
            return;
          }
        },
        (error) => {
          this.assessmentChartLoading = false;
        },
      );
  }

  private getAssessmentChartWithoutSubjects(subject: string, studyYearId: number, courseId: number, schoolYearSettingsId: number): void {
    this.schoolAdminDashboardService
      .getAssessmentChart(subject, studyYearId, courseId, schoolYearSettingsId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (chartData) => {
          if (chartData) this.processAssessmentChart(chartData);
        },
        (error) => {
          this.assessmentChartLoading = false;
        },
      );
  }

  private processAssessmentChart(chartData: ISAChart) {
    this.assessmentResultsMark = chartData.averageGrade;
    this.assessmentResultsMarkChange.emit(this.assessmentResultsMark);
    this.assessmentResultDate = `${moment(chartData.startDate).format('D MMMM YYYY')} - ${moment(chartData.endDate).format('D MMMM YYYY')}`;
    const chartResults = {
      grades: [],
      averageBenchmark: [],
    };
    const month = [];
    const lineChartLabels = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    this.chartAverageBenchmark = chartData.averageBenchmark;
    chartData.grades.forEach((item) => {
      chartResults.averageBenchmark.push(chartData.averageBenchmark);
      if (item.gradeLabel === '-') {
        chartResults.grades.push(null)
      } else {
        chartResults.grades.push(item.gradeLabel)
      };
      month.push(lineChartLabels[item.month - 1]);
    });

    if (chartResults.grades.filter(item => !!item).length === 0) chartResults.averageBenchmark = [];

    this.assessmentResultsChart = chartResults;
    this.assessmentResultsChartMonth = month;
    this.assessmentChartLoading = false;
  }
}
